import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import AttitudeBar from "@/components/base/AttitudeBar";
import PollCard from "@/components/base/PollCard";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/categories";
import { formatNumber } from "@/utils/format";

const totalWeight = topics.reduce((sum, t) => sum + t.poll.totalVotes, 0);
const weighted = topics.reduce(
  (acc, t) => ({
    support: acc.support + t.attitude.support * t.poll.totalVotes,
    neutral: acc.neutral + t.attitude.neutral * t.poll.totalVotes,
    oppose: acc.oppose + t.attitude.oppose * t.poll.totalVotes,
  }),
  { support: 0, neutral: 0, oppose: 0 }
);

const aggregate = {
  support: Math.round(weighted.support / totalWeight),
  neutral: Math.round(weighted.neutral / totalWeight),
  oppose: Math.round(weighted.oppose / totalWeight),
};

export default function Polls() {
  const [active, setActive] = useState("all");

  const list = useMemo(() => {
    const filtered =
      active === "all" ? topics : topics.filter((t) => t.categorySlug === active);
    return [...filtered].sort((a, b) => b.poll.totalVotes - a.poll.totalVotes);
  }, [active]);

  useSeo({
    title: "投票风向 - 投票结果展示与舆论态度分布 | 爆料看点",
    description:
      "集中查看爆料看点所有话题的投票结果展示与舆论态度分布，支持、中立、反对三段并列呈现，一眼看清网友站队与风向变化。",
    keywords:
      "投票结果展示,舆论态度分布,投票风向,爆料看点,网友神评论",
    path: "/polls",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "投票风向",
        url: "https://qrwho.cn/polls",
        inLanguage: "zh-CN",
        description:
          "集中展示各话题的投票结果与舆论态度分布，包含支持、中立、反对三段占比。",
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: list.length,
          itemListElement: list.map((topic, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: topic.poll.question,
            url: `https://qrwho.cn/topics/${topic.id}`,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://qrwho.cn/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "投票风向",
            item: "https://qrwho.cn/polls",
          },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <Breadcrumbs items={[{ label: "投票风向" }]} />
          <h1 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            投票风向
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            这里是所有话题的投票结果展示页。每场投票都保留了完整选项、得票比例与舆论态度分布，
            遇到争议事件想先看站队和风向，从这一页开始最省时间。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-12">
        <section
          aria-label="整体舆论态度分布"
          className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
        >
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <span className="font-label text-xs tracking-[0.18em] text-foreground-500 uppercase">
                整体风向
              </span>
              <h2 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                综合 {topics.length} 个话题、{formatNumber(totalWeight)} 份投票的态度分布
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                支持与反对的比例越接近，说明话题争议度越高；中立段越长，说明还有大量网友在观望，
                尚未形成明确站队。
              </p>
            </div>
            <div className="flex flex-col justify-center rounded-lg bg-background-100 p-5">
              <AttitudeBar attitude={aggregate} />
            </div>
          </div>
        </section>

        <div className="mt-8">
          <SectionHeading
            eyebrow="按栏目筛选"
            title="选择你想看的方向"
            subtitle="筛选后只显示该栏目下的投票，排序会按参与人数从高到低自动调整。"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setActive("all")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                active === "all"
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部栏目
            </button>
            {categories.map((category) => (
              <button
                key={category.slug}
                type="button"
                onClick={() => setActive(category.slug)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                  active === category.slug
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {list.map((topic) => (
              <div key={topic.id} className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-label text-xs text-foreground-500">
                  <span className="rounded-full bg-secondary-50 px-2.5 py-1 text-secondary-800">
                    {topic.category}
                  </span>
                  <Link
                    to={`/topics/${topic.id}`}
                    className="cursor-pointer truncate transition-colors hover:text-primary-600"
                  >
                    {topic.title}
                  </Link>
                </div>
                <PollCard poll={topic.poll} />
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-heading text-sm font-bold text-foreground-950">
                      舆论态度分布
                    </h3>
                    <span className="font-label text-xs text-foreground-500">
                      基于 {formatNumber(topic.poll.totalVotes)} 份投票
                    </span>
                  </div>
                  <div className="mt-4">
                    <AttitudeBar attitude={topic.attitude} size="sm" />
                  </div>
                  <Link
                    to={`/topics/${topic.id}`}
                    className="mt-4 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap font-label text-xs text-secondary-700 transition-colors hover:text-primary-600"
                  >
                    查看该话题的评论区高能回复
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-arrow-right-line" aria-hidden="true" />
                    </span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}